import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

function DefaultPagination({ active, setActive, totalPages }) {
  const getItemProps = (index) => ({
    className: `relative inline-flex items-center rounded-lg px-4 py-2 border text-sm font-medium ${active === index ? 'bg-blue-400 text-white' : 'bg-white text-black'
      }`,
    onClick: () => setActive(index),
  });

  const next = () => {
    if (active === totalPages) return;
    setActive(active + 1);
  };

  const prev = () => {
    if (active === 1) return;
    setActive(active - 1);
  };

  return (
    <div className="flex items-center gap-4">
      <button
        className={'flex items-center gap-2 px-4 py-2 text-blue-500'}
        onClick={prev}
        disabled={active === 1}
      >
        <svg
          className="h-4 w-4 text-blue-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Previous
      </button>
      <div className="flex items-center gap-2">
        {Array.from({ length: totalPages }, (_, i) => (
          <button key={i + 1} {...getItemProps(i + 1)}>
            {i + 1}
          </button>
        ))}
      </div>
      <button
        className={'flex items-center gap-2 px-4 py-2 text-blue-500'}
        onClick={next}
        disabled={active === totalPages}
      >
        Next
        <svg
          className="h-4 w-4 text-blue-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

export default function ChooseServiceComponent() {
  const [data, setData] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [bookingData, setBookingData] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('https://fboothapi.azurewebsites.net/api/service/customer');
        const services = response.data.map(service => ({ ...service, Quantity: '0' }));
        setData(services);
        setTotalPages(Math.ceil(services.length / itemsPerPage));
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const storedBookingData = sessionStorage.getItem('bookingData');
    if (storedBookingData) {
      setBookingData(JSON.parse(storedBookingData));
    }
    const storedServices = sessionStorage.getItem('selectedServices');
    if (storedServices) {
      setSelectedServices(JSON.parse(storedServices));
    }
  }, []);

  const handleAddService = (service) => {
    const updatedServices = [...selectedServices];
    const existingService = updatedServices.find(item => item.serviceID === service.serviceID);
    if (existingService) {
      existingService.Quantity = (parseInt(existingService.Quantity) + parseInt(service.Quantity)).toString();
    } else {
      updatedServices.push(service);
    }
    setSelectedServices(updatedServices);
    sessionStorage.setItem('selectedServices', JSON.stringify(updatedServices));
  };

  const handleIncrement = (id) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.serviceID === id ? { ...item, Quantity: (parseInt(item.Quantity) + 1).toString() } : item
      )
    );
  };

  const handleDecrement = (id) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.serviceID === id && parseInt(item.Quantity) > 0 ? { ...item, Quantity: (parseInt(item.Quantity) - 1).toString() } : item
      )
    );
  };

  const handleCreateBooking = async () => {
    if (!bookingData) {
      console.error('No booking data found in session.');
      return;
    }

    const { booth, email, date, starttime, endtime, selectedBranch } = bookingData;
    const startTime = new Date(`${date} ${starttime}`);
    const endTime = new Date(`${date} ${endtime}`);

    startTime.setHours(startTime.getHours() + 7);
    endTime.setHours(endTime.getHours() + 7);

    const serviceList = selectedServices.reduce((acc, service) => {
      acc[service.serviceID] = parseInt(service.Quantity);
      return acc;
    }, {});

    const bookingPayload = {
      boothID: booth.boothID,
      customerEmail: email,
      serviceList,
      startTime,
      endTime,
    };
    const token = sessionStorage.getItem('userToken');
    console.log('Booking Payload:', JSON.stringify(bookingPayload, null, 2)); // Log the payload to check the structure
    try {
      const response = await axios.post('https://fboothapi.azurewebsites.net/api/booking/customer-booking', bookingPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Include the token in the request headers
        }
      });
      console.log('Booking created successfully:', response.data);
      const newBookingData = {
        ...bookingData,
        bookingID: response.data.bookingID, // Assuming the response contains bookingID
        bookingServices: response.data.bookingServices, // Assuming the response contains bookingServices
        bookingType: response.data.bookingType, // Assuming the response contains bookingType
        boothID: response.data.boothID, // Assuming the response contains boothID
        createdDate: response.data.createdDate, // Assuming the response contains createdDate
        customerID: response.data.customerID, // Assuming the response contains customerID
        customerReferenceID: response.data.customerReferenceID, // Assuming the response contains customerReferenceID
        endTime: response.data.endTime, // Assuming the response contains endTime
        hireBoothFee: response.data.hireBoothFee, // Assuming the response contains hireBoothFee
        isCancelled: response.data.isCancelled, // Assuming the response contains isCancelled
        lastModified: response.data.lastModified, // Assuming the response contains lastModified
        paymentAmount: response.data.paymentAmount, // Assuming the response contains paymentAmount
        paymentStatus: response.data.paymentStatus, // Assuming the response contains paymentStatus
        startTime: response.data.startTime, // Assuming the response contains startTime
        status: response.data.status // Assuming the response contains status
      };
      sessionStorage.setItem('bookingData', JSON.stringify(newBookingData));
      navigate('/payment'); // Assuming you have a confirmation page
    } catch (error) {
      if (error.response) {
        console.error('Error response data:', error.response.data); // Log the response data for debugging
        setError(error.response.data.message || 'Cannot booking this booth, try again please!');
      } else {
        console.error('Error creating booking:', error.message);
        setError('Cannot booking this booth, try again please!');
      }
    }
  };

  const paginatedData = data.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  return (
    <div className="sm:ml-64">
      <div className="items-center p-10">
        <div className="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 pr-10 lg:px-8">
          <div className="align-middle inline-block min-w-full shadow overflow-hidden bg-white shadow-dashboard px-8 pt-3 mt-10 mb-20 rounded-lg">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-6 py-6 border-b-2 border-gray-300 text-left leading-4 text-blue-500 tracking-wider">NO</th>
                  <th className="px-6 py-6 border-b-2 border-gray-300 text-left text-sm leading-4 text-blue-500 tracking-wider">Service</th>
                  <th className="px-6 py-6 border-b-2 border-gray-300 text-left text-sm leading-4 text-blue-500 tracking-wider">Price</th>
                  <th className="px-6 py-6 border-b-2 border-gray-300 text-left text-sm leading-4 text-blue-500 tracking-wider">Quantity</th>
                  <th className="px-6 py-6 border-b-2 border-gray-300"></th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {paginatedData.map((item, index) => (
                  <tr key={item.serviceID}>
                    <td className="px-6 py-6 whitespace-no-wrap border-b border-gray-500">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm leading-5 text-gray-800">
                            {(activePage - 1) * itemsPerPage + index + 1}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-no-wrap border-b border-gray-500">
                      <div className="text-sm leading-5 text-blue-900">{item.serviceName}</div>
                    </td>
                    <td className="px-6 py-6 whitespace-no-wrap border-b text-blue-900 border-gray-500 text-sm leading-5">{item.servicePrice}</td>
                    <td className="px-6 py-6 whitespace-no-wrap border-b text-blue-900 border-gray-500 text-sm leading-5">
                      <div className="relative flex items-center">
                        <button
                          type="button"
                          onClick={() => handleDecrement(item.serviceID)}
                          className="flex-shrink-0 bg-gray-100 inline-flex items-center justify-center border border-gray-300 rounded-md h-5 w-5 focus:ring-gray-100 focus:ring-2 focus:outline-none"
                        >
                          <svg className="w-2.5 h-2.5 text-gray-900" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 2">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h16" />
                          </svg>
                        </button>
                        <input
                          type="text"
                          className="flex-shrink-0 text-gray-900 border-0 bg-transparent text-sm font-normal focus:outline-none focus:ring-0 max-w-[2.5rem] text-center"
                          value={item.Quantity}
                          readOnly
                        />
                        <button
                          type="button"
                          onClick={() => handleIncrement(item.serviceID)}
                          className="flex-shrink-0 bg-gray-100 hover:bg-gray-200 inline-flex items-center justify-center border border-gray-300 rounded-md h-5 w-5 focus:ring-gray-100 focus:ring-2 focus:outline-none"
                        >
                          <svg className="w-2.5 h-2.5 text-gray-900" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 1v16M1 9h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-no-wrap text-right border-b border-gray-500 text-sm leading-5">
                      <button
                        className="px-5 py-2 border-blue-500 border text-blue-500 rounded transition duration-300 hover:bg-blue-700 hover:text-white focus:outline-none"
                        onClick={() => handleAddService(item)}
                      >
                        Add Service
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sm:flex-1 sm:flex sm:items-center justify-end mt-4 work-sans pb-4">
              <DefaultPagination
                active={activePage}
                setActive={setActivePage}
                totalPages={totalPages}
              />
            </div>
          </div>
        </div>

        <div className="p-4">
          <h2 className="text-xl font-bold mb-4">Services Out of Booth:</h2>
          {selectedServices.length > 0 ? (
            <ul>
              {selectedServices.map((service, index) => (
                <li key={index} className="mb-2 flex">
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-2">
                      <p className="text-sm px-3 leading-none text-gray-800">
                        <span className="text-gray-400"> </span> {service.serviceName}
                      </p>
                      <p> - </p>
                      <p className="text-sm px-3 leading-none text-gray-800">
                        <span className="text-gray-400">Quantity: </span>{service.Quantity}
                      </p>
                    </div>
                    <div className="ml-40">
                      <p className="px-3 text-base xl:text-lg">
                        <span className="text-gray-400"></span> {service.servicePrice}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No services selected yet.</p>
          )}
        </div>

        <div className="flex justify-center flex-col md:flex-row flex-col items-stretch w-full space-y-4 md:space-y-0 md:space-x-6 xl:space-x-8">
          <div className="flex flex-col px-4 py-6 md:p-6 xl:p-8 w-full bg-gray-50 space-y-6 shadow rounded-lg">
            {error && (
              <div className="bg-red-500 text-white px-4 py-2 rounded">
                {error}
              </div>
            )}
            <button
              className="w-1/3 px-5 py-2 border-blue-500 border text-blue-500 rounded transition duration-300 hover:bg-blue-700 hover:text-white focus:outline-none"
              onClick={handleCreateBooking}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
